import React, { useState, useEffect } from 'react';
import { withStyles, useTheme } from '@material-ui/core/styles';
import { GroupWork, Settings } from '@material-ui/icons';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { orange } from '@material-ui/core/colors';
import { Grid, Tabs, Tab, CircularProgress, Button } from '@material-ui/core';
import MuiDialogContent from '@material-ui/core/DialogContent';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import PropTypes from 'prop-types';
import DataTable from './auditTrailTable';
import GroupsSection from './groupsSection';
import AutomationAuditContent from './automationAudit';
import { getColumns, isUnread } from './auditTrailColumns';
import { hasAutomationTabAccess } from '../../../../helpers/user';

const useStyles = () => ({
  root: {
    margin: 0,
    '& .audit-trail-groups': {
      backgroundColor: '#F4F4F4',
    },
    '& .audit-trail-groups-unread': {
      backgroundColor: '#9c27b017',
    },
    '& .audit-trail-groups-new-value': {
      color: '#9C27B0',
    },
    '& .audit-trail-groups-new-value-dot': {
      color: orange[500],
      marginLeft: '-5px',
      padding: '3px',
    },
    '& .table-cell-trucate': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .audit-trail-header-icons': {
      fontSize: 16,
    },
  },
  rootDark: {
    margin: 0,
    '& .audit-trail-groups': {
      backgroundColor: '#212121',
      fontWeight: 'bold',
    },
    '& .audit-trail-groups-unread': {
      backgroundColor: '#9c27b017',
    },
    '& .audit-trail-groups-new-value': {
      fontWeight: 'bold',
      color: '#9C27B0',
    },
    '& .audit-trail-groups-new-value-dot': {
      color: orange[500],
      marginLeft: '-5px',
      padding: '3px',
    },
    '& .table-cell-trucate': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .audit-trail-header-icons': {
      fontSize: 16,
    },
  },
  contentTable: {
    borderBottom: '1px solid #E9E9E9',
  },
  loadingContent: {
    padding: '15px',
  },
});

const AUDIT_TABS = {
  CHANGES_PER_GROUP: 0,
  GROUPS: 1,
  AUTOMATION: 2,
};

const getRowClassName = (id, unread) => {
  return isUnread(id, unread) ? 'audit-trail-groups-unread' : '';
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ minHeight: '550px' }}
      {...other}
    >
      {value === index && (
        <div p={3}>
          <div>{children}</div>
        </div>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.any.isRequired,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

const AuditDialogContent = withStyles(useStyles)(props => {
  const theme = useTheme();
  const { classes, data, isFetching, unread = [], eventId } = props;
  const [value, setValue] = useState(AUDIT_TABS.CHANGES_PER_GROUP);
  const [displaySkybox] = useState(true);
  const [displayGroup, setDisplayGroup] = useState('');
  const [auditTrailRecords, setAuditTrailRecords] = useState([]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  function DisplayGroupMode(groupName) {
    setDisplayGroup(groupName);
  }

  function resetGroupName() {
    setDisplayGroup('');
  }

  useEffect(() => {
    if (displaySkybox === true) {
      setAuditTrailRecords([]);
      if (data) {
        setAuditTrailRecords(data);
      }
    } else if (displaySkybox === false) {
      if (data) {
        setAuditTrailRecords(data.filter(item => item.actor_id !== 'SKYBOX'));
      }
    }
  }, [displaySkybox, data]);

  return (
    <MuiDialogContent disableTypography>
      <Tabs
        value={value}
        onChange={handleChange}
        indicatorColor="primary"
        textColor="primary"
        aria-label="simple tabs example"
        TabIndicatorProps={{
          style: {
            height: '1px',
          },
        }}
      >
        <Tab
          label={
            <div>
              <GroupWork style={{ verticalAlign: 'middle', marginBottom: 0, marginTop: 0, marginRight: 3 }} />
              Changes per Group
            </div>
          }
        />
        <Tab
          label={
            <div>
              <GroupWork style={{ verticalAlign: 'middle', marginBottom: 0, marginTop: 0, marginRight: 3 }} />
              Groups
            </div>
          }
        />
        {hasAutomationTabAccess() && (
          <Tab
            label={
              <div>
                <Settings style={{ verticalAlign: 'middle', marginBottom: 0, marginTop: 0, marginRight: 3 }} />
                Automation
              </div>
            }
          />
        )}
      </Tabs>

      <div
        style={{
          minHeight: '30px',
          marginRight: '38px',
          marginBottom: '-5px',
          marginTop: '-10px',
          paddingTop: '0px',
          paddingBottom: '0px',
        }}
      >
        <p>
          {/* <SkyboxSelectionModelToggler SeatGeekSelectionMode={SeatGeekSelectionMode} displaySkybox={displaySkybox} /> */}
          {displayGroup !== '' && value === AUDIT_TABS.CHANGES_PER_GROUP && (
            <Button
              variant="text"
              style={{
                textTransform: 'capitalize',
                color: theme.palette.type === 'dark' ? '#BA68C8' : '#9C27B0',
                marginTop: '-40px',
              }}
              onClick={() => {
                resetGroupName();
              }}
            >
              <ArrowBackIcon />
              Go Back
            </Button>
          )}
        </p>
      </div>
      <TabPanel value={value} index={AUDIT_TABS.CHANGES_PER_GROUP}>
        {isFetching && auditTrailRecords.length === 0 ? (
          <div className={classes.loadingContent}>
            <Grid item xs={3}>
              <CircularProgress />
            </Grid>
          </div>
        ) : (
          <div>
            {data ? (
              <div className={theme.palette.type === 'light' ? classes.root : classes.rootDark}>
                <GroupsSection
                  groupsInfo={auditTrailRecords}
                  paletteType={theme.palette.type}
                  displayGroupMode={DisplayGroupMode}
                  displayGroup={displayGroup}
                  displaySkybox={displaySkybox}
                />
              </div>
            ) : (
              <div className={classes.loadingContent}>
                <h2>No groups audit changes</h2>
              </div>
            )}
          </div>
        )}
      </TabPanel>
      <TabPanel value={value} index={AUDIT_TABS.GROUPS}>
        <Grid className={classes.contentTable}>
          {isFetching && auditTrailRecords.length === 0 ? (
            <div className={classes.loadingContent}>
              <Grid item xs={3}>
                <CircularProgress />
              </Grid>
            </div>
          ) : (
            <div
              className={theme.palette.type === 'light' ? classes.root : classes.rootDark}
              style={{
                height: 550,
                width: '100%',
                padding: '15px',
                marginTop: '-15px',
              }}
            >
              <DataTable
                data={auditTrailRecords}
                columns={getColumns(unread)}
                unread={unread}
                getRowClassName={getRowClassName}
                defaultRowClassName={false}
              />
            </div>
          )}
        </Grid>
      </TabPanel>
      <TabPanel value={value} index={AUDIT_TABS.AUTOMATION}>
        <Grid className={classes.contentTable}>
          <div className={theme.palette.type === 'light' ? classes.root : classes.rootDark}>
            <AutomationAuditContent event={eventId} />
          </div>
        </Grid>
      </TabPanel>
    </MuiDialogContent>
  );
});

AuditDialogContent.propTypes = {
  eventId: PropTypes.object.isRequired,
  isFetching: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  unread: PropTypes.array.isRequired,
};

export default AuditDialogContent;
